// WFS 2.0.0 feature extraction for zone land data (Environment Agency servers).
//
// `srsName=urn:ogc:def:crs:EPSG::4326` is required — the EA servers return
// EPSG:27700 (British National Grid) coordinates otherwise. Under the urn CRS
// form the BBOX parameter axis order is lat,lon — passing lon,lat silently
// returns zero features. The GeoJSON response body itself is standard
// [lng, lat] regardless (verified against captured responses).
//
// BBOX over-fetches slightly; true intersection is computed client-side with
// turf in intersect.js.
//
// The server page size is capped at WFS_MAX_FEATURES. A single field rarely
// intersects more than a handful of polygons, so this is generous headroom; but
// if the server still has more to give (numberMatched > numberReturned) we flag
// the result `truncated` so downstream can caveat the reading rather than
// silently under-reporting coverage.

import { fetchTextWithTimeout } from "../fetchWithTimeout";

// Max features requested per WFS page. Raised from 200 so realistic fields never
// truncate; the truncated flag catches the rare case that they do.
const WFS_MAX_FEATURES = 1000;

/**
 * Compute the [west, south, east, north] bounding box of a GeoJSON Polygon.
 */
export function polygonBbox(polygon) {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const ring of polygon.coordinates) {
    for (const [lng, lat] of ring) {
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
    }
  }
  return [west, south, east, north];
}

/**
 * Fetch features from a WFS 2.0.0 layer whose BBOX intersects a zone polygon.
 * @param {object} layer entry from ZONE_LAND_LAYERS (protocol 'wfs')
 * @param {object} zonePolygon GeoJSON Polygon geometry (WGS84, [lng, lat])
 * @returns {Promise<{features: Array, truncated: boolean}>} GeoJSON Features
 *   (WGS84) and whether the server had more features than it returned.
 */
export async function fetchWfsFeatures(layer, zonePolygon) {
  const [west, south, east, north] = polygonBbox(zonePolygon);

  const params = new URLSearchParams({
    service: "WFS",
    version: "2.0.0",
    request: "GetFeature",
    typeNames: layer.typeName,
    outputFormat: "application/json",
    srsName: "urn:ogc:def:crs:EPSG::4326",
    count: String(WFS_MAX_FEATURES),
    // lat,lon axis order under urn:ogc:def:crs:EPSG::4326
    bbox: `${south},${west},${north},${east},urn:ogc:def:crs:EPSG::4326`,
  });

  // Bounded, headers and body alike: an EA server that accepts the connection
  // and then never answers used to leave the Land characteristics panel and the
  // soil legend loading for the rest of the session (BBB-527). Reading text and
  // parsing it here rather than calling response.json() is what lets the body
  // read sit inside the same deadline; see fetchWithTimeout.js.
  const { ok, status, body } = await fetchTextWithTimeout(
    `${layer.url}?${params.toString()}`,
    undefined,
    `WFS query for ${layer.id}`
  );

  if (!ok) {
    throw new Error(`WFS query failed for ${layer.id}: HTTP ${status}`);
  }

  let data;
  try {
    data = JSON.parse(body);
  } catch {
    throw new Error(`WFS query failed for ${layer.id}: the response was not JSON.`);
  }

  const features = data.features || [];

  // WFS 2.0.0 reports numberMatched (total available) and numberReturned (this
  // page). numberMatched can be the string "unknown" on some servers, so fall
  // back to comparing the returned count against the page cap.
  const matched = Number(data.numberMatched);
  const returned = Number.isFinite(Number(data.numberReturned))
    ? Number(data.numberReturned)
    : features.length;
  const truncated = Number.isFinite(matched)
    ? matched > returned
    : features.length >= WFS_MAX_FEATURES;

  return { features, truncated };
}
